/**
 * TAH PDP CTA orchestrator
 *
 * Owns the final rendering of the add-to-cart button on Try-at-Home product
 * pages. theme.js:updateCartButton sets the authoritative state via
 * `data-cta-state="available|notify|contact"` on the ATC button; this file
 * reads that attribute and dispatches the correct visible copy, classes, and
 * auxiliary-link visibility.
 *
 * Gated to TAH PDPs via the Liquid `{% if %}` wrapping the `script_tag` in
 * snippets/product-template-v2.liquid. Reads Liquid context (product handle,
 * buy-now link) from data-attributes on the `.x-variant-picker` container.
 *
 * Prior impl lived inline inside product-template-v2.liquid with a 150ms
 * setTimeout race guard against theme.js. Extracted here + switched to the
 * `cta:state:changed` CustomEvent dispatched by updateCartButton so we no
 * longer guess at timing.
 */
(function () {
  var productBlock = document.querySelector('.x-variant-picker');
  if (!productBlock) return;

  var productHandle = productBlock.getAttribute('data-product-handle') || '';
  var buyNowHandle = productBlock.getAttribute('data-buy-now-handle') || '';
  var isBridesmaid = productHandle.indexOf('bridesmaid') !== -1;
  var tahPrice = isBridesmaid ? '$10' : '$15';

  productBlock.classList.add('tah-pdp-cta');

  function updateCTAState() {
    var addBtn = productBlock.querySelector('.add-to-cart');
    if (!addBtn) return;

    var btnText = addBtn.querySelector('[data-add-to-cart-text]');
    var buyAfterTry = productBlock.querySelector('.x-grid a.btn.x-btn-blue');
    var buyNowLink = productBlock.querySelector('.tah-pdp-buy-now-link');
    var notifySimilar = productBlock.querySelector('.tah-pdp-notify-similar');
    var hasSizeSelected = productBlock.querySelector('input[type="radio"]:checked') !== null;
    var state = addBtn.getAttribute('data-cta-state');

    // No size selected yet wins over whatever state theme.js set — user hasn't
    // committed to a variant so default copy + disabled.
    if (!hasSizeSelected) {
      if (btnText) btnText.textContent = 'Try at Home — ' + tahPrice;
      addBtn.disabled = true;
      addBtn.removeAttribute('data-tah-notify');
      if (buyAfterTry) buyAfterTry.style.display = '';
      if (buyNowLink) buyNowLink.style.display = '';
      if (notifySimilar) notifySimilar.classList.remove('is-visible');
      return;
    }

    switch (state) {
      case 'contact':
        // State 3 — size + color combo doesn't exist.
        if (btnText) btnText.textContent = 'Contact a Stylist';
        addBtn.disabled = false;
        addBtn.setAttribute('data-tah-notify', 'true');
        if (buyAfterTry) buyAfterTry.style.display = 'none';
        if (buyNowLink) buyNowLink.style.display = 'none';
        if (notifySimilar) notifySimilar.classList.remove('is-visible');
        break;

      case 'notify':
        // State 2 — variant exists, out with a bride.
        if (btnText) btnText.textContent = 'Notify Me When My Size Is Available';
        addBtn.disabled = false;
        addBtn.setAttribute('data-tah-notify', 'true');
        if (buyAfterTry) buyAfterTry.style.display = 'none';
        if (buyNowLink) buyNowLink.style.display = 'none';
        if (notifySimilar) notifySimilar.classList.add('is-visible');
        break;

      case 'available':
      default:
        // State 1 — variant exists and is in stock. Also the fallback when
        // theme.js hasn't run yet on first paint.
        addBtn.removeAttribute('data-tah-notify');
        if (btnText) btnText.textContent = 'Try at Home — ' + tahPrice;
        if (buyAfterTry) buyAfterTry.style.display = '';
        if (buyNowLink) buyNowLink.style.display = '';
        if (notifySimilar) notifySimilar.classList.remove('is-visible');
    }
  }

  function setupCTAExtras() {
    var btnGrid = productBlock.querySelector('.x-grid.grid-cols-2, .x-grid.grid-cols-1');
    if (!btnGrid || productBlock.querySelector('.tah-pdp-buy-now-link')) return;

    var addBtn = btnGrid.querySelector('.add-to-cart');
    if (addBtn && !addBtn.querySelector('.tah-pdp-heart')) {
      var heartBtn = document.createElement('span');
      heartBtn.className = 'tah-pdp-heart';
      heartBtn.setAttribute('aria-label', 'Add to Favorites');
      heartBtn.innerHTML = '<svg viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
      heartBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var wkBtn = productBlock.querySelector('.abc-wishlist-button .wk-button');
        if (wkBtn) wkBtn.click();
        heartBtn.classList.toggle('is-active');
      });
      addBtn.appendChild(heartBtn);
    }

    var buyNowLink = document.createElement('a');
    buyNowLink.className = 'tah-pdp-buy-now-link';
    buyNowLink.href = '/products/' + (buyNowHandle || productHandle);
    buyNowLink.textContent = 'Already tried this dress? Buy Now!';
    btnGrid.parentNode.insertBefore(buyNowLink, btnGrid.nextSibling);

    var similarLink = document.createElement('div');
    similarLink.className = 'tah-pdp-notify-similar';
    similarLink.innerHTML = '<a href="/collections/try-at-home-wedding-dresses">Try a Similar Dress →</a>';
    buyNowLink.parentNode.insertBefore(similarLink, buyNowLink.nextSibling);
  }

  function setupCheckboxReveal() {
    var checkbox = productBlock.querySelector('.wp-current-variant');
    var addBtn = productBlock.querySelector('.add-to-cart');
    if (!checkbox || !addBtn) return;

    // Capture phase is intentional: must run BEFORE Shopify's own form-submit
    // handler on the ATC button so we can block submission when the agreement
    // isn't checked. Don't demote to bubble without reverifying that behavior.
    addBtn.addEventListener(
      'click',
      function (e) {
        if (addBtn.hasAttribute('data-tah-notify')) return;
        var cbInput = checkbox.querySelector('#i_agree_to_buy');
        if (cbInput && !cbInput.checked && !checkbox.classList.contains('is-revealed')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          checkbox.classList.add('is-revealed');
          cbInput.focus();
          return false;
        }
      },
      true
    );
  }

  // State settled — theme.js dispatches this on every variant change.
  document.addEventListener('cta:state:changed', updateCTAState);

  function init() {
    setupCTAExtras();
    setupCheckboxReveal();
    // First-paint backstop: theme.js's variant:change may not have fired yet.
    // One 300ms tick lets updateCartButton run before we render.
    setTimeout(updateCTAState, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
